import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Approval } from "../lib/status";
import { askUrl, serviceUrl } from "../lib/links";
import { TopbarSettings } from "../components/TopbarSettings";
import { apiFetch } from "../lib/api";

type Outcome = { ok: boolean; detail: string };

/**
 * The stuck-import inbox.
 *
 * Every action reports what actually happened. v1 popped an `alert()` on
 * failure and otherwise silently refreshed, so a refused import looked
 * identical to a successful one.
 */
export function DecideView() {
  const navigate = useNavigate();
  const [items, setItems] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState<string>("");
  const [outcomes, setOutcomes] = useState<Record<string, Outcome>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch("/api/approvals");
      if (res.status === 401) return;
      if (!res.ok) throw new Error(`status ${res.status}`);
      const j = (await res.json()) as { approvals: Approval[] };
      setItems(j.approvals);
      setError("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  /*
   * Poll while the tab is visible. A stuck queue row can clear itself (the
   * *arr retries, the fixer wins the next pass), and an inbox that only ever
   * loaded once showed work that was already done.
   */
  useEffect(() => {
    void load();
    const tick = setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, 15_000);
    const onVisible = () => {
      if (document.visibilityState === "visible") void load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(tick);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  const decide = async (id: string, action: "import" | "redownload" | "ignore") => {
    setBusy(id);
    try {
      const res = await apiFetch("/api/decide", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, action }),
      });
      if (res.status === 401) return;
      const j = (await res.json()) as Outcome & { error?: string };
      const outcome: Outcome = { ok: Boolean(j.ok), detail: j.detail ?? j.error ?? "" };
      setOutcomes((o) => ({ ...o, [id]: outcome }));
      // Only drop the card when the server confirmed it acted.
      if (outcome.ok) setTimeout(() => setItems((prev) => prev.filter((x) => x.id !== id)), 1400);
    } catch (e) {
      setOutcomes((o) => ({
        ...o,
        [id]: { ok: false, detail: e instanceof Error ? e.message : String(e) },
      }));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="page">
      <header className="topbar">
        <h1 className="topbar__title">Decide</h1>
        <div className="topbar__right">
          {items.length > 0 && <span className="pill pill--warn">{items.length} waiting</span>}
          <button type="button" className="btn btn--ghost btn--icon" onClick={() => void load()} aria-label="Refresh">
            <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12a9 9 0 1 1-2.6-6.4M21 3v6h-6" />
            </svg>
          </button>
          <TopbarSettings />
        </div>
      </header>

      <div className="page-body">
        <div className="page-inner stack stagger">
          {error && <div className="banner banner--fault">{error}</div>}
          {loading && !items.length && (
            <div className="stack">
              <div className="skel skel--card" />
              <div className="skel skel--card" />
            </div>
          )}
          {!loading && !items.length && (
            <div className="empty">
              <div className="empty__mark" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12.5 9.5 17 19 7" />
                </svg>
              </div>
              <strong>Nothing waiting</strong>
              <div>
                This inbox shows both the fixer's unresolved imports and any queue row
                Sonarr or Radarr has flagged itself. Neither has anything right now.
              </div>
            </div>
          )}

          {items.map((item) => {
            const outcome = outcomes[item.id];
            return (
              <article key={item.id} className="card decide">
                <div className="decide__meta">
                  <span className="pill">{item.app ?? "unknown"}</span>
                  {/* Where this came from matters: a queue row is live, a fixer
                      row is a file the 5-minute task wrote and gave up on. */}
                  <span className={`pill ${item.source === "queue" ? "pill--warn" : ""}`}>
                    {item.source === "queue" ? "stuck in queue" : "fixer"}
                  </span>
                  {item.state && <span className="pill mono">{item.state}</span>}
                  {item.recommended && (
                    <span className="pill pill--accent">suggests {item.recommended}</span>
                  )}
                </div>
                <h2 className="decide__title">{item.expected || item.title || item.id}</h2>
                {item.stuckReason && <p className="decide__why">{item.stuckReason}</p>}
                {item.messages?.length ? (
                  <ul className="decide__files mono">
                    {item.messages.slice(0, 4).map((m, i) => (
                      <li key={i}>{m}</li>
                    ))}
                  </ul>
                ) : null}
                {item.outputPath && <p className="decide__path mono">{item.outputPath}</p>}
                {item.files?.length ? (
                  <ul className="decide__files mono">
                    {item.files.slice(0, 4).map((f, i) => (
                      <li key={i} className="truncate">{f}</li>
                    ))}
                  </ul>
                ) : null}

                {outcome && (
                  <div className={`decide__outcome ${outcome.ok ? "is-ok" : "is-bad"}`}>
                    {outcome.ok ? "✓ " : "✕ "}
                    {outcome.detail}
                  </div>
                )}

                <div className="decide__actions">
                  <button
                    type="button"
                    className={`btn ${item.recommended === "redownload" ? "" : "btn--primary"}`}
                    disabled={busy === item.id}
                    onClick={() => void decide(item.id, "import")}
                  >
                    Import
                  </button>
                  <button
                    type="button"
                    className="btn btn--danger"
                    disabled={busy === item.id}
                    onClick={() => void decide(item.id, "redownload")}
                  >
                    Redownload
                  </button>
                  <button
                    type="button"
                    className="btn"
                    disabled={busy === item.id}
                    onClick={() => void decide(item.id, "ignore")}
                  >
                    Ignore
                  </button>
                </div>

                {/* Neither decision is obvious for every row, so both escape
                    hatches are on the card: look at it in the *arr, or ask. */}
                <div className="decide__links">
                  {serviceUrl(item.url) && (
                    <a href={serviceUrl(item.url)} target="_blank" rel="noreferrer">
                      Open in {item.app ?? "the instance"} →
                    </a>
                  )}
                  <button
                    type="button"
                    className="linkish"
                    onClick={() =>
                      navigate(
                        askUrl(
                          `${item.app ?? "An *arr"} is stuck on "${item.expected || item.title || item.id}". Reason given: ${item.stuckReason ?? "none"}. What should I do?`,
                        ),
                      )
                    }
                  >
                    Ask about this →
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}
